import React from "react";
import { PASSWORD_MAX_LEN, PASSWORD_MIN_LEN } from "@kkp/api-client";

export type PasswordRule = {
  key: string;
  label: string;
  test: (s: string) => boolean;
};

// eslint-disable-next-line react-refresh/only-export-components -- shared rule descriptors colocated with the component.
export const PASSWORD_RULES: PasswordRule[] = [
  {
    key: "length",
    label: `${PASSWORD_MIN_LEN}–${PASSWORD_MAX_LEN} characters`,
    test: (s) => s.length >= PASSWORD_MIN_LEN && s.length <= PASSWORD_MAX_LEN,
  },
  {
    key: "upper",
    label: "Contains an uppercase letter",
    test: (s) => /\p{Lu}/u.test(s),
  },
  {
    key: "lower",
    label: "Contains a lowercase letter",
    test: (s) => /\p{Ll}/u.test(s),
  },
  { key: "digit", label: "Contains a digit", test: (s) => /\d/.test(s) },
  {
    key: "symbol",
    label: "Contains a symbol",
    test: (s) => /[^\p{L}\p{N}]/u.test(s),
  },
];

// eslint-disable-next-line react-refresh/only-export-components -- pure helper colocated with the component for shared password-rule logic.
export function isPasswordValid(s: string): boolean {
  return PASSWORD_RULES.every((r) => r.test(s));
}

// eslint-disable-next-line react-refresh/only-export-components -- pure helper colocated with the component for shared password-rule logic.
export function isPasswordMatch(password: string, confirm: string): boolean {
  return confirm.length > 0 && password === confirm;
}

type MatchState = "neutral" | "ok" | "bad";

type Props = {
  password: string;
  confirm?: string;
  showMatch?: boolean;
  /** Optional heading rendered above the rules; omitted when not provided. */
  heading?: string;
  className?: string;
};

export function PasswordRules({
  password,
  confirm = "",
  showMatch = false,
  heading,
  className,
}: Props): React.ReactElement {
  const matchState: MatchState =
    !showMatch || confirm.length === 0
      ? "neutral"
      : password === confirm
        ? "ok"
        : "bad";

  return (
    <div className={className}>
      {heading ? (
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[color:rgba(255,255,255,0.6)]">
          {heading}
        </p>
      ) : null}
      <ul className="grid gap-1 text-sm">
        {PASSWORD_RULES.map((rule) => {
          const ok = rule.test(password);
          return (
            <li
              key={rule.key}
              className={ok ? "text-emerald-600" : "text-slate-500"}
              aria-live="polite"
            >
              <span aria-hidden="true">{ok ? "✓" : "•"}</span> {rule.label}
            </li>
          );
        })}
        {showMatch ? (
          <li
            key="match"
            className={
              matchState === "ok"
                ? "text-emerald-600"
                : matchState === "bad"
                  ? "text-red-500"
                  : "text-slate-500"
            }
            aria-live="polite"
          >
            <span aria-hidden="true">
              {matchState === "ok" ? "✓" : matchState === "bad" ? "✗" : "•"}
            </span>{" "}
            {matchState === "bad"
              ? "Passwords do not match"
              : "Passwords match"}
          </li>
        ) : null}
      </ul>
    </div>
  );
}
